import { PrecisionDate } from "./precisionDate.js";
/**
 * Performance monitor tracks rolling average frame-time and frame-time variance over a user defined sliding-window
 */
var PerformanceMonitor = /** @class */ (function () {
    /**
     * constructor
     * @param frameSampleSize The number of samples required to saturate the sliding window
     */
    function PerformanceMonitor(frameSampleSize) {
        if (frameSampleSize === void 0) { frameSampleSize = 30; }
        this._enabled = true;
        this._rollingFrameTime = new RollingAverage(frameSampleSize);
    }
    /**
     * Samples current frame
     * @param timeMs A timestamp in milliseconds of the current frame to compare with other frames
     */
    PerformanceMonitor.prototype.sampleFrame = function (timeMs) {
        if (timeMs === void 0) { timeMs = PrecisionDate.Now; }
        if (!this._enabled) {
            return;
        }
        if (this._lastFrameTimeMs != null) {
            var dt = timeMs - this._lastFrameTimeMs;
            this._rollingFrameTime.add(dt);
        }
        this._lastFrameTimeMs = timeMs;
    };
    Object.defineProperty(PerformanceMonitor.prototype, "averageFrameTime", {
        /**
         * Returns the average frame time in milliseconds over the sliding window (or the subset of frames sampled so far)
         */
        get: function () {
            return this._rollingFrameTime.average;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerformanceMonitor.prototype, "averageFrameTimeVariance", {
        /**
         * Returns the variance frame time in milliseconds over the sliding window (or the subset of frames sampled so far)
         */
        get: function () {
            return this._rollingFrameTime.variance;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerformanceMonitor.prototype, "instantaneousFrameTime", {
        /**
         * Returns the frame time of the most recent frame
         */
        get: function () {
            return this._rollingFrameTime.history(0);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerformanceMonitor.prototype, "averageFPS", {
        /**
         * Returns the average framerate in frames per second over the sliding window (or the subset of frames sampled so far)
         */
        get: function () {
            return 1000.0 / this._rollingFrameTime.average;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerformanceMonitor.prototype, "instantaneousFPS", {
        /**
         * Returns the average framerate in frames per second using the most recent frame time
         */
        get: function () {
            var history = this._rollingFrameTime.history(0);
            if (history === 0) {
                return 0;
            }
            return 1000.0 / history;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PerformanceMonitor.prototype, "isSaturated", {
        /**
         * Returns true if enough samples have been taken to completely fill the sliding window
         */
        get: function () {
            return this._rollingFrameTime.isSaturated();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Enables contributions to the sliding window sample set
     */
    PerformanceMonitor.prototype.enable = function () {
        this._enabled = true;
    };
    /**
     * Disables contributions to the sliding window sample set
     * Samples will not be interpolated over the disabled period
     */
    PerformanceMonitor.prototype.disable = function () {
        this._enabled = false;
        //clear last sample to avoid interpolating over the disabled period when next enabled
        this._lastFrameTimeMs = null;
    };
    Object.defineProperty(PerformanceMonitor.prototype, "isEnabled", {
        /**
         * Returns true if sampling is enabled
         */
        get: function () {
            return this._enabled;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Resets performance monitor
     */
    PerformanceMonitor.prototype.reset = function () {
        //clear last sample to avoid interpolating over the disabled period when next enabled
        this._lastFrameTimeMs = null;
        //wipe record
        this._rollingFrameTime.reset();
    };
    return PerformanceMonitor;
}());
export { PerformanceMonitor };
/**
 * RollingAverage
 *
 * Utility to efficiently compute the rolling average and variance over a sliding window of samples
 */
var RollingAverage = /** @class */ (function () {
    /**
     * constructor
     * @param length The number of samples required to saturate the sliding window
     */
    function RollingAverage(length) {
        this._samples = new Array(length);
        this.reset();
    }
    /**
     * Adds a sample to the sample set
     * @param v The sample value
     */
    RollingAverage.prototype.add = function (v) {
        //http://en.wikipedia.org/wiki/Algorithms_for_calculating_variance
        var delta;
        //we need to check if we've already wrapped round
        if (this.isSaturated()) {
            //remove bottom of stack from mean
            var bottomValue = this._samples[this._pos];
            delta = bottomValue - this.average;
            this.average -= delta / (this._sampleCount - 1);
            this._m2 -= delta * (bottomValue - this.average);
        }
        else {
            this._sampleCount++;
        }
        //add new value to mean
        delta = v - this.average;
        this.average += delta / this._sampleCount;
        this._m2 += delta * (v - this.average);
        //set the new variance
        this.variance = this._m2 / (this._sampleCount - 1);
        this._samples[this._pos] = v;
        this._pos++;
        this._pos %= this._samples.length; //positive wrap around
    };
    /**
     * Returns previously added values or null if outside of history or outside the sliding window domain
     * @param i Index in history. For example, pass 0 for the most recent value and 1 for the value before that
     * @return Value previously recorded with add() or null if outside of range
     */
    RollingAverage.prototype.history = function (i) {
        if (i >= this._sampleCount || i >= this._samples.length) {
            return 0;
        }
        var i0 = this._wrapPosition(this._pos - 1.0);
        return this._samples[this._wrapPosition(i0 - i)];
    };
    /**
     * Returns true if enough samples have been taken to completely fill the sliding window
     * @return true if sample-set saturated
     */
    RollingAverage.prototype.isSaturated = function () {
        return this._sampleCount >= this._samples.length;
    };
    /**
     * Resets the rolling average (equivalent to 0 samples taken so far)
     */
    RollingAverage.prototype.reset = function () {
        this.average = 0;
        this.variance = 0;
        this._sampleCount = 0;
        this._pos = 0;
        this._m2 = 0;
    };
    /**
     * Wraps a value around the sample range boundaries
     * @param i Position in sample range, for example if the sample length is 5, and i is -3, then 2 will be returned.
     * @return Wrapped position in sample range
     */
    RollingAverage.prototype._wrapPosition = function (i) {
        var max = this._samples.length;
        return ((i % max) + max) % max;
    };
    return RollingAverage;
}());
export { RollingAverage };
//# sourceMappingURL=performanceMonitor.js.map